import { watch } from 'vue';

import { useCartStore } from '@/shared/pinia/modules/cart.ts';
import { trackKitAddedToCart } from '@/shared/tracking/kit-add-to-cart.ts';

import { initAllCartComponents, initCartLineKitDetails } from './index.ts';

const cartLoadingClassName = 'is-loading';
const cartWrapperSelector = '.js-cart-wrapper';
const miniCartMountSelector = '.js-mini-cart-mount';

const cartLineInputSelector = '.js-cart-line-input';
const cartLineErrorSelector = '.js-cart-line-error';

const isMiniCart = !!document.querySelector(miniCartMountSelector);

// cart line item updates
const initiateCartEventListener = () => {
  const cartStore = useCartStore();

  document.getElementsByName('updates[]').forEach((el) => {
    const input = el as HTMLInputElement;
    const lineNumber = Number.parseInt(input.dataset.lineNumber ?? '');
    input.addEventListener('change', () => {
      const quantity = Number.parseInt(input.value);
      if (!Number.isNaN(quantity) && !Number.isNaN(lineNumber)) {
        cartStore.cartChangeQty(lineNumber, quantity);
      }
    });

    input.addEventListener('keydown', (event) => {
      const isNumericKey = !Number.isNaN(Number.parseInt(event.key));
      const allowedSpecialKeys = [
        'Tab',
        'Backspace',
        'Enter',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown'
      ];

      if (
        isNumericKey ||
        allowedSpecialKeys.includes(event.key) ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      event.preventDefault();
    });
  });
};

export const setCart = () => {
  const cartStore = useCartStore();
  let isFirstCartWatch = true;

  window.Hobbii!.cart = {
    ...window.Hobbii!.cart,
    changeQty: cartStore.cartChangeQty
  };

  initiateCartEventListener();

  // Listen for cart refresh from external scripts (e.g. Shopify app)
  window.addEventListener('hobbii.cart.refresh', () => {
    cartStore.getCart();
    if (window.Hobbii?.productPage?.isKit) {
      trackKitAddedToCart();
    }
  });

  /**
   * Watch for cart loading state to add the loading class and disable main cart inputs.
   * Cleanup also runs here as a safety net for paths where loadingCart goes false
   * without a cart mutation (e.g. validation errors). The cart watcher below also
   * removes the loading state after HTML replacement in the normal flow.
   */
  watch(
    () => cartStore.loadingCart,
    (loading) => {
      if (!loading) {
        document.querySelectorAll(cartWrapperSelector).forEach((el) => {
          el.classList.remove(cartLoadingClassName);
        });
        document
          .querySelectorAll<HTMLInputElement>(
            `${cartWrapperSelector} ${cartLineInputSelector}`
          )
          .forEach((el) => {
            el.disabled = false;
          });
        return;
      }

      if (isFirstCartWatch) {
        return;
      }

      document.querySelectorAll(cartWrapperSelector).forEach((el) => {
        el.classList.add(cartLoadingClassName);
      });

      // Only disable main cart inputs (Liquid-rendered)
      document
        .querySelectorAll<HTMLInputElement>(
          `${cartWrapperSelector} ${cartLineInputSelector}`
        )
        .forEach((el) => {
          el.disabled = true;
        });
    }
  );

  /**
   * Watch for cart changes to update the cart HTML and conditionally re-initialize Vue components
   * - Full replacement: re-initializes all components
   * - Main cart partial update: no re-initialization (containers weren't replaced)
   */
  watch(
    () => cartStore.cart,
    async () => {
      if (isFirstCartWatch) {
        isFirstCartWatch = false;
        return;
      }

      if (isMiniCart) return;

      const isCartEmpty = cartStore.cart?.item_count === 0;
      let wasFullReplacement = false;

      // For empty cart, replace entire form (no gift collection to preserve)
      if (isCartEmpty) {
        const cartHtmlResponse = await fetch(
          `${window.Shopify.routes.root}cart?view=async`
        );
        const cartHtml = await cartHtmlResponse.text();
        const form = document.querySelector<HTMLElement>(
          'form[action="/cart"]'
        );
        if (form) {
          form.outerHTML = cartHtml;
          wasFullReplacement = true;
        }
      } else {
        const contentBelowGifts = document.querySelector<HTMLElement>(
          '.js-cart-main-items-and-summary'
        );

        if (contentBelowGifts) {
          // Partial update: only replace content below gift collection
          const cartHtmlResponse = await fetch(
            `${window.Shopify.routes.root}cart?view=main-items-and-summary-async`
          );
          const cartHtml = await cartHtmlResponse.text();
          contentBelowGifts.outerHTML = cartHtml;
          initCartLineKitDetails();
        } else {
          // Transitioning from empty to having items - replace entire form
          const cartHtmlResponse = await fetch(
            `${window.Shopify.routes.root}cart?view=async`
          );
          const cartHtml = await cartHtmlResponse.text();
          const form = document.querySelector<HTMLElement>(
            'form[action="/cart"]'
          );
          if (form) {
            form.outerHTML = cartHtml;
            wasFullReplacement = true;
          }
        }
      }

      // Re-initialize Vue components if the entire form was replaced
      setTimeout(() => {
        if (wasFullReplacement) {
          initAllCartComponents(true);
        }
      }, 200);

      if (cartStore.errors.length) {
        cartStore.errors.forEach((error) => {
          document
            .querySelectorAll(
              `[data-cart-line-key="${error.lineKey}"] ${cartLineErrorSelector}`
            )
            .forEach((element) => {
              element.classList.remove('hidden');

              const messageEl = element?.querySelector(
                `${cartLineErrorSelector}-msg`
              );

              if (messageEl) {
                messageEl.textContent = error.message;
              }
            });
        });
        cartStore.clearErrors();
      }

      initiateCartEventListener();

      // Remove loading state from main cart DOM
      document.querySelectorAll(cartWrapperSelector).forEach((el) => {
        el.classList.remove(cartLoadingClassName);
      });
      document
        .querySelectorAll<HTMLInputElement>(
          `${cartWrapperSelector} ${cartLineInputSelector}`
        )
        .forEach((el) => {
          el.disabled = false;
        });
    }
  );

  return initAllCartComponents();
};
